import { useEffect, useState, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Plus, Trash2, Edit2, PoundSterling, Package, Box, ShoppingBag, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

// Provider Dashboard, Products tab.
//
// Phase 0 of the retail upsell stack: provider-owned product catalogue
// CRUD. No upsell wiring yet, no Stripe split, no order surface, those
// land in Phase 1 + 2.
//
// Available on all tiers (Free + Pro) per founder spec, the retail
// add-on is a value lift Zuriya is making available to every stylist
// regardless of plan.

const EMPTY_FORM = {
  name: '', description: '', price: '', stock_qty: 0,
  image_url: '', weight_g: 0, vat_rate: 0, is_active: true,
};

export function ProductsTab({ provider }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | product_id
  const [form, setForm] = useState(EMPTY_FORM);
  const isVatRegistered = !!provider?.is_vat_registered;
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/products/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((prev) => ({ ...prev, image_url: res.data.url }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get('/products');
      setProducts(res.data || []);
    } catch (e) {
      toast.error('Could not load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const startNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditing('new');
  };

  const startEdit = (p) => {
    setForm({
      name: p.name, description: p.description || '',
      price: String(p.price), stock_qty: p.stock_qty || 0,
      image_url: p.image_url || '', weight_g: p.weight_g || 0,
      vat_rate: p.vat_rate || 0, is_active: !!p.is_active,
    });
    setEditing(p.product_id);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };


  const save = async () => {
    if (!form.name.trim() || !form.price || parseFloat(form.price) <= 0) {
      toast.error('Name and a price > £0 are required');
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      price: parseFloat(form.price),
      stock_qty: parseInt(form.stock_qty, 10) || 0,
      image_url: form.image_url.trim(),
      weight_g: parseInt(form.weight_g, 10) || 0,
      vat_rate: isVatRegistered ? parseFloat(form.vat_rate) || 0 : 0,
      is_active: !!form.is_active,
    };
    try {
      if (editing === 'new') {
        await api.post('/products', payload);
        toast.success('Product added');
      } else {
        await api.patch(`/products/${editing}`, payload);
        toast.success('Product updated');
      }
      cancel();
      fetchProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save product');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="products-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] flex items-center gap-2">
            <ShoppingBag className="h-6 w-6 text-[#D4AF37]" />
            Your Products
          </h2>
          <p className="text-sm text-stone-500 mt-1 max-w-xl">
            Add the retail items you sell to clients alongside your services, scalp oils, bonnets, edge control, anything you ship or hand over in the chair. We&apos;ll plug these into your booking funnel as add-ons in the next release.
          </p>
        </div>
        {!editing && (
          <Button onClick={startNew} className="btn-primary shrink-0" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Product
          </Button>
        )}
      </div>

      {/* VAT registration banner */}
      {!isVatRegistered && (
        <div className="rounded-lg bg-[#F9F6F0] border border-stone-200 p-4 text-sm text-stone-600" data-testid="products-vat-banner">
          <p>
            <strong className="text-[#1A1A1A]">VAT is off.</strong> Most stylists earning under £90k don&apos;t need to register, so prices on your products are treated as VAT-free.
            If you ARE VAT-registered, toggle it on in <strong>Settings → VAT</strong> and a VAT rate field will appear on each product.
          </p>
        </div>
      )}

      {/* Add/Edit form */}
      {editing && (
        <div className="card-surface p-6 border border-[#D4AF37]/30" data-testid="product-form">
          <h3 className="font-medium text-[#1A1A1A] mb-4">
            {editing === 'new' ? 'New product' : 'Edit product'}
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Scalp Soothe Oil 50ml"
                className="input-zuriya"
                data-testid="product-name"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Tell clients what's in it and how to use it"
                className="input-zuriya"
                rows={2}
                data-testid="product-description"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Price *</label>
              <div className="relative">
                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  type="number" min="0" step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="14.50"
                  className="input-zuriya pl-9"
                  data-testid="product-price"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Stock on hand</label>
              <Input
                type="number" min="0"
                value={form.stock_qty}
                onChange={(e) => setForm({ ...form, stock_qty: e.target.value })}
                className="input-zuriya"
                data-testid="product-stock"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Product image</label>

              {form.image_url ? (
                <div className="flex items-start gap-3 mb-2">
                  <img
                    src={form.image_url}
                    alt="Product preview"
                    className="w-24 h-24 object-cover rounded-lg border border-[#E6E4DD]"
                    data-testid="product-image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: '' })}
                    className="text-xs text-stone-500 hover:text-rose-600 flex items-center gap-1"
                    data-testid="product-image-remove"
                  >
                    <X className="h-3 w-3" /> Remove
                  </button>
                </div>
              ) : (
                <label
                  className="flex flex-col items-center justify-center gap-1 w-full p-4 border-2 border-dashed border-[#E6E4DD] hover:border-[#D4AF37] rounded-lg cursor-pointer transition-colors mb-2"
                  data-testid="product-image-dropzone"
                >
                  <Upload className="h-5 w-5 text-stone-400" />
                  <span className="text-sm text-stone-600">
                    {uploadingImage ? 'Uploading…' : 'Click to upload (JPG, PNG, WEBP — max 5 MB)'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files?.[0])}
                    disabled={uploadingImage}
                    className="hidden"
                    data-testid="product-image-file"
                  />
                </label>
              )}

              <details className="text-xs text-stone-500">
                <summary className="cursor-pointer hover:text-[#1A1A1A]">Or paste an image URL</summary>
                <Input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  className="input-zuriya mt-2"
                  data-testid="product-image-url"
                />
              </details>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Weight (g)</label>
              <Input
                type="number" min="0"
                value={form.weight_g}
                onChange={(e) => setForm({ ...form, weight_g: e.target.value })}
                className="input-zuriya"
                data-testid="product-weight"
              />
            </div>
            {isVatRegistered && (
              <div>
                <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">VAT rate</label>
                <select
                  value={form.vat_rate}
                  onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) })}
                  className="w-full input-zuriya"
                  data-testid="product-vat-rate"
                >
                  <option value="0">Zero-rated (0%)</option>
                  <option value="0.05">Reduced (5%)</option>
                  <option value="0.2">Standard (20%)</option>
                </select>
              </div>
            )}
            <div className="sm:col-span-2 flex items-center justify-between bg-[#F9F6F0] rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-[#1A1A1A]">Listed live</p>
                <p className="text-xs text-stone-500">When off, clients can&apos;t add this product to a booking.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="product-active"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <Button variant="outline" onClick={cancel} className="border-[#E6E4DD]" data-testid="cancel-product-btn">
              Cancel
            </Button>
            <Button onClick={save} className="btn-primary" data-testid="save-product-btn">
              {editing === 'new' ? 'Add Product' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Product list */}
      {loading ? (
        <p className="text-stone-500 text-sm">Loading...</p>
      ) : products.length === 0 && !editing ? (
        <div className="card-surface p-10 text-center" data-testid="products-empty">
          <Box className="h-12 w-12 text-stone-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-stone-600 font-medium mb-1">No products yet</p>
          <p className="text-sm text-stone-500 max-w-md mx-auto">
            Add the first item you sell to clients. Once you have a couple, we&apos;ll show them as recommended add-ons during checkout.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="products-list">
          {products.map((p) => (
            <div
              key={p.product_id}
              className={`card-surface p-4 flex items-center gap-4 ${!p.is_active ? 'opacity-60' : ''}`}
              data-testid={`product-row-${p.product_id}`}
            >
              <div className="w-16 h-16 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="h-6 w-6 text-stone-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-[#1A1A1A] truncate">{p.name}</h4>
                  {!p.is_active && (
                    <span className="text-xs px-2 py-0.5 bg-stone-200 rounded-full text-stone-600">Paused</span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                  <span className="font-medium text-[#1A1A1A]">£{p.price.toFixed(2)}</span>
                  <span>{p.stock_qty} in stock</span>
                  {isVatRegistered && p.vat_rate > 0 && (
                    <span>VAT {Math.round(p.vat_rate * 100)}%</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => startEdit(p)}
                  className="p-2 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1A1A1A] transition-colors"
                  data-testid={`edit-product-${p.product_id}`}
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => remove(p)}
                  className="p-2 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600 transition-colors"
                  data-testid={`delete-product-${p.product_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
